<div class="geofence-map" [class.geofence-map--readonly]="readonly">
  @if (!readonly) {
    <div class="geofence-map__search">
      <div class="geofence-map__search-field">
        <i class="fa fa-search"></i>
        <input
          type="search"
          role="combobox"
          autocomplete="off"
          aria-autocomplete="list"
          [attr.aria-controls]="listboxId"
          [attr.aria-expanded]="suggestions.length > 0"
          [attr.aria-activedescendant]="activeSuggestion >= 0 ? listboxId + '-' + activeSuggestion : null"
          [ngModel]="placeQuery"
          [disabled]="!mapReady"
          (ngModelChange)="onQueryChange($event)"
          (keydown)="onQueryKeydown($event)"
          (keyup.enter)="submitSearch()"
          (blur)="closeSuggestions()"
          placeholder="Find a place to zoom to — city, area or landmark" />
        @if (placeQuery) {
          <button type="button" class="geofence-map__search-clear" (click)="clearPlaceSearch()" aria-label="Clear">
            <i class="fa fa-times"></i>
          </button>
        }

        @if (suggestions.length) {
          <!-- mousedown is swallowed so the input keeps focus long enough for the click to land -->
          <ul
            class="geofence-map__suggestions"
            [id]="listboxId"
            role="listbox"
            (mousedown)="$event.preventDefault()">
            @for (suggestion of suggestions; track suggestion.prediction.placeId; let i = $index) {
              <li role="option" [id]="listboxId + '-' + i" [attr.aria-selected]="i === activeSuggestion">
                <button
                  type="button"
                  class="geofence-map__suggestion"
                  [class.geofence-map__suggestion--active]="i === activeSuggestion"
                  (click)="chooseSuggestion(suggestion)">
                  <i class="fa fa-map-marker-alt"></i>
                  <span class="geofence-map__suggestion-text">
                    <span class="geofence-map__suggestion-primary">{{ suggestion.primary }}</span>
                    @if (suggestion.secondary) {
                      <span class="geofence-map__suggestion-secondary">{{ suggestion.secondary }}</span>
                    }
                  </span>
                </button>
              </li>
            }
          </ul>
        }
      </div>
      <button type="button" class="gf-btn" [disabled]="!mapReady || !placeQuery.trim() || searching" (click)="submitSearch()">
        {{ searching ? 'Searching…' : 'Go' }}
      </button>
    </div>

    @if (placeResults.length) {
      <ul class="geofence-map__results">
        <li class="geofence-map__results-head">Which one?</li>
        @for (place of placeResults; track place.label) {
          <li>
            <button type="button" (click)="goToPlace(place.result)">{{ place.label }}</button>
          </li>
        }
      </ul>
    }

    <div class="geofence-map__toolbar">
      @if (drawMode === 'none') {
        <button type="button" class="gf-btn" [disabled]="!mapReady" (click)="startDrawArea()">
          <i class="fa fa-draw-polygon"></i>
          Add area
        </button>

        <button type="button" class="gf-btn" [disabled]="!mapReady" (click)="startDrawExclusion()">
          <i class="fa fa-ban"></i>
          Add excluded area
        </button>

        <button
          type="button"
          class="gf-btn gf-btn--ghost"
          [disabled]="!mapReady || !areas.length"
          (click)="fitToAreas()">
          <i class="fa fa-compress-arrows-alt"></i>
          Fit to areas
        </button>
      } @else {
        <span class="geofence-map__drawing">
          <i class="fa fa-crosshairs"></i>
          Placing {{ drawMode === 'exclusion' ? 'an excluded area' : 'an area' }} ·
          {{ draftPointCount }} corner{{ draftPointCount === 1 ? '' : 's' }}
        </span>

        <button type="button" class="gf-btn gf-btn--active" [disabled]="!canFinishDrawing" (click)="finishDrawing()">
          <i class="fa fa-check"></i>
          Finish {{ drawMode === 'exclusion' ? 'excluded area' : 'area' }}
        </button>

        <button type="button" class="gf-btn" [disabled]="!draftPointCount" (click)="undoLastPoint()">
          <i class="fa fa-undo"></i>
          Undo point
        </button>

        <button type="button" class="gf-btn gf-btn--ghost" (click)="cancelDrawing()">Cancel</button>
      }
    </div>

    @if (hint) {
      <p class="geofence-map__hint">{{ hint }}</p>
    }
  }

  <div class="geofence-map__body">
    <div class="geofence-map__canvas" [style.height.px]="heightPx">
      <div #mapContainer class="geofence-map__canvas-inner"></div>

      @if (loadError) {
        <div class="geofence-map__overlay geofence-map__overlay--error">{{ loadError }}</div>
      } @else if (!mapReady) {
        <div class="geofence-map__overlay">Loading map…</div>
      } @else if (readonly && !areas.length) {
        <div class="geofence-map__overlay">This geofence has no shape to show.</div>
      }
    </div>

    @if (!readonly) {
      <aside class="geofence-map__areas" [style.max-height.px]="heightPx">
        <h4 class="geofence-map__areas-title">
          Areas in this geofence
          <span class="geofence-map__count">{{ areas.length }}</span>
        </h4>

        @if (!areas.length) {
          <p class="geofence-map__empty">
            Nothing drawn yet. Use <strong>Add area</strong> to draw the region members must be inside.
          </p>
        }

        @for (area of areas; track area.id; let i = $index) {
          <div class="geofence-area" [class.geofence-area--selected]="area.id === selectedAreaId">
            <div class="geofence-area__head" (click)="selectArea(area)">
              <span class="geofence-area__name">{{ areaLabel(i) }}</span>
              <span class="geofence-area__size">{{ areaSize(area) }}</span>
              <button
                type="button"
                class="geofence-area__remove"
                title="Remove this area"
                (click)="removeArea(area); $event.stopPropagation()">
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <div class="geofence-area__meta">{{ area.outer.length }} points</div>

            @for (hole of area.holes; track $index; let h = $index) {
              <div class="geofence-hole">
                <i class="fa fa-ban"></i>
                <span>Excluded area {{ h + 1 }}</span>
                <button
                  type="button"
                  class="geofence-area__remove"
                  title="Remove this exclusion"
                  (click)="removeHole(area, h)">
                  <i class="fa fa-times"></i>
                </button>
              </div>
            }
          </div>
        }
      </aside>
    }
  </div>
</div>
